import styled from "@emotion/styled";
import { VisibilityOutlined } from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconDownload, IconPlus } from "@tabler/icons-react";
import moment from "moment";
import { useModal } from "mui-modal-provider";
import { useCallback, useMemo, useState } from "react";
import NoResultFound from "../../components/NoResultFound";
import Pagination from "../../components/Pagination";
import SubCard from "../../components/cards/SubCard";
import ViewDigitalDocument from "../cases/ViewDigitalDocument";
import { getS3Url } from "../../../utils/s3";

const Documents = ({ selectedCase, selectedOption, fromCase = false }: any) => {
  const { showModal } = useModal();
  const CustomTableRow = styled(TableRow)(({ theme }) => ({
    height: "50px",
  }));
  const CustomTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: 0,
    paddingBottom: 0,
    width: 130,
    overflow: "hidden",
    textOverflow: "ellipsis",
  }));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState();
  const [documentLoading, setDocumentLoading] = useState<any>(null);

  // Memoized document filtering
  const filteredDocuments = useMemo(() => {
    if (!selectedCase?.documents) return [];
    setPage(0);
    switch (selectedOption.value) {
      case "chatDocuments":
        return selectedCase.documents.filter(
          (doc: any) => doc?.isMessageAttachment && doc?.isDeleted === false
        );
      case "uploadedDocuments":
        return selectedCase.documents.filter(
          (doc: any) => !doc?.isMessageAttachment && doc?.isDeleted === false
        );
      case "repAgreement":
        return selectedCase?.docuSignUrl
          ? [
              {
                title: "Rep Agreement",
                type: "application/pdf", // Assuming the type is PDF
                document: [
                  { url: getS3Url(selectedCase.docuSignUrl), name: "Rep Agreement.pdf" },
                ],
                uploadedBy: fromCase
                  ? selectedCase?.lawyer
                  : selectedCase?.createdBy,
                status: selectedCase?.docuSignStatus,
                createdAt: selectedCase?.createdAt,
                updatedAt: selectedCase?.updatedAt,
              },
            ]
          : [];
      case "report":
        return selectedCase?.dpsReportURL
          ? [
              {
                title: "Dps Report",
                type: "application/pdf", // Assuming the type is PDF
                document: [
                  { url: getS3Url(selectedCase?.dpsReportURL), name: "Dps Report.pdf" },
                ],
                uploadedBy: fromCase
                  ? selectedCase?.lawyer
                  : selectedCase?.createdBy,
                createdAt: selectedCase?.createdAt,
                updatedAt: selectedCase?.updatedAt,
              },
            ]
          : [];
      default:
        return selectedCase?.documents;
    }
  }, [selectedCase, selectedOption]);

  const checkDocuSignStatus: any = (status: any) => {
    let color = "warning";

    switch (status) {
      case "COMPLETED":
        color = "success";
        break;

      case "DECLINED":
        color = "error";
        break;
    }

    return color;
  };

  // const handleViewDocument = useCallback(
  //   (data: any) => {
  //     const modal: any = showModal(ViewDigitalDocument, {
  //       onClose: () => modal.hide(),
  //       documentUrl: data?.document[data?.document?.length - 1]?.url,
  //       mimeType: data?.type,
  //     });
  //   },
  //   [showModal]
  // );
  const handleViewDocument = useCallback(
    (data: any) => {
      setDocumentLoading(data?._id); // Set loading state
      const modal: any = showModal(ViewDigitalDocument, {
        onClose: () => modal.hide(),
        fileUrl: getS3Url(data?.document[data?.document?.length - 1]?.url),
        mimetype: data?.type,
        title: data?.title,
        closeLoading: setDocumentLoading,
      });
    },
    [showModal]
  );

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      setIsDownloading(true);
      const response = await fetch(fileUrl, { method: "GET" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName); // Force download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke object URL after download
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      setIsDownloading(false);
    } catch (error) {
      setIsDownloading(false);
    }
  };

  function shortenFileName(fileName: string, maxLength: number = 20): string {
    const extensionIndex = fileName.lastIndexOf(".");

    // Handle files without extensions
    if (extensionIndex === -1 || fileName.length <= maxLength) {
      return fileName; // Return original if it's shorter than maxLength
    }

    const name = fileName.substring(0, extensionIndex); // File name without extension
    const extension = fileName.substring(extensionIndex); // File extension

    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Start part (first N characters) and end part (last N characters of name)
    const startPart = name.substring(0, 15);
    const endPart = name.substring(name.length - 5);

    return `${startPart}..${endPart}${extension}`;
  }
  const paginatedDocuments = useMemo(() => {
    return filteredDocuments.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredDocuments, page, rowsPerPage]);
  return (
    <Grid item xs={12} p={2}>
      <SubCard
        title={selectedOption?.title}
        // secondary={
        //   <Tooltip title="Add documents">
        //     {/* {isStaff ? (
        //         <Typography></Typography>
        //       ) : ( */}
        //     <Button
        //       sx={{
        //         color: "green",
        //         cursor: "pointer",
        //       }}
        //       // onClick={handleAddDigitalDocument}
        //     >
        //       <IconPlus />
        //     </Button>
        //     {/* )} */}
        //   </Tooltip>
        // }
      >
        {!!filteredDocuments.length ? (
          <>
            <Stack
              sx={{
                overflowY: "auto",
                maxHeight: "calc(70vh - 220px)",
              }}
            >
              <Table sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <CustomTableRow>
                    <CustomTableCell>
                      {" "}
                      <Typography variant="h5">Created At</Typography>
                    </CustomTableCell>
                    <CustomTableCell>
                      {" "}
                      <Typography variant="h5">Updated At</Typography>
                    </CustomTableCell>
                    <CustomTableCell>
                      {" "}
                      <Typography variant="h5">Title</Typography>
                    </CustomTableCell>

                    <CustomTableCell>
                      {" "}
                      <Typography variant="h5">Uploaded By</Typography>
                    </CustomTableCell>
                    {selectedOption.value === "repAgreement" && (
                      <CustomTableCell>
                        {" "}
                        <Typography variant="h5">Status</Typography>
                      </CustomTableCell>
                    )}
                    <CustomTableCell>
                      {" "}
                      <Typography variant="h5">Action</Typography>
                    </CustomTableCell>
                  </CustomTableRow>
                </TableHead>
                <TableBody>
                  {paginatedDocuments.map(
                    (row: any, index: any) =>
                      !row?.isDeleted && (
                        <CustomTableRow key={index}>
                          <CustomTableCell>
                            {moment(row?.createdAt).format("MM/DD/YYYY")}
                          </CustomTableCell>
                          <CustomTableCell>
                            {moment(row?.updatedAt).format("MM/DD/YYYY")}
                          </CustomTableCell>
                          <CustomTableCell>
                            {shortenFileName(row?.title)}
                          </CustomTableCell>
                          <CustomTableCell>
                            {row?.uploadedBy?.firstName
                              ? row?.uploadedBy?.firstName +
                                "" +
                                row?.uploadedBy?.lastName
                              : row?.uploadedBy?.userName}
                          </CustomTableCell>
                          {selectedOption.value === "repAgreement" && (
                            <CustomTableCell>
                              {row?.status ? (
                                <Chip
                                  label={
                                    row.status.charAt(0).toUpperCase() +
                                    row.status.slice(1).toLowerCase()
                                  }
                                  color={checkDocuSignStatus(row.status)} // You can customize this color based on status
                                  variant="filled" // Use "filled" if you want a solid background
                                />
                              ) : (
                                ""
                              )}
                            </CustomTableCell>
                          )}
                          <CustomTableCell>
                            {isDownloading && selectedDoc === index ? (
                              <CircularProgress
                                size={24}
                                sx={{ mt: 1, mr: 2 }}
                              />
                            ) : (
                              <IconButton
                                onClick={() => {
                                  setSelectedDoc(index);
                                  handleDownload(
                                    getS3Url(row?.document?.[0]?.url),
                                    row?.document?.[0]?.name
                                  );
                                }}
                              >
                                <Tooltip title={"Download Document"}>
                                  <IconDownload
                                    color="grey"
                                    cursor={"pointer"}
                                  />
                                </Tooltip>
                              </IconButton>
                            )}
                            <Tooltip title={"View Document"}>
                              <IconButton
                                onClick={() => handleViewDocument(row)}
                              >
                                {documentLoading === row?._id ? (
                                  <CircularProgress size={"25px"} />
                                ) : (
                                  <VisibilityOutlined />
                                )}
                              </IconButton>
                            </Tooltip>
                          </CustomTableCell>
                        </CustomTableRow>
                      )
                  )}
                </TableBody>
              </Table>
            </Stack>
            {!(
              selectedOption?.value === "repAgreement" ||
              selectedOption?.value === "report"
            ) && (
              <Pagination
                count={filteredDocuments.length}
                page={page}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />
            )}
          </>
        ) : (
          <NoResultFound>No Document Found</NoResultFound>
        )}
      </SubCard>
    </Grid>
  );
};

export default Documents;
